import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation, Trans } from "react-i18next";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    CircularProgress,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    History as HistoryIcon,
} from "@mui/icons-material";
import {
    getVpnUsers,
    createVpnUser,
    updateVpnUser,
    deleteVpnUser,
    getVpnUserAccessLogs,
    getVpnSessions,
} from "../api/vpnApi";
import { getTenants } from "../api/tenantApi";
import { formatDate } from "../utils/i18nFormat";

export default function VpnUsers() {
    const { t } = useTranslation();
    const accessToken = useSelector((s) => s.session?.accessToken);
    const me = useSelector((s) => s.session?.me);
    const isSuperAdmin = me?.global_role === "superadmin";
    // Backend (_require_vpn_user_manager) tenant/sub-tenant admin'e de izin veriyor,
    // sadece "*" (tüm filo) hedefi superadmin'e özel — bkz. isSuperAdmin kullanılan diğer yerler.
    const canManageVpnUsers = isSuperAdmin || me?.tenant_role === "admin";

    const [vpnUsers, setVpnUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vpnUsersTotal, setVpnUsersTotal] = useState(0);
    const [vpnUsersOffset, setVpnUsersOffset] = useState(0);
    const [vpnUsersLimit] = useState(20);
    const [loadingMore, setLoadingMore] = useState(false);
    const [vpnUsersSearch, setVpnUsersSearch] = useState("");

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        username: "",
        email: "",
        full_name: "",
        allowed_tenants: [],
        password: "",
    });
    const [creating, setCreating] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({
        email: "",
        full_name: "",
        allowed_tenants: [],
        is_active: true,
        password: "",
    });
    const [updating, setUpdating] = useState(false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [showLogsDialog, setShowLogsDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [accessLogs, setAccessLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [accessLogsSearch, setAccessLogsSearch] = useState("");

    const [showSessionsDialog, setShowSessionsDialog] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsTotal, setSessionsTotal] = useState(0);
    const [sessionsOffset, setSessionsOffset] = useState(0);
    const [sessionsLimit] = useState(50);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
    const [sessionsSearch, setSessionsSearch] = useState("");

    useEffect(() => {
        if (accessToken) {
            fetchVpnUsers();
            fetchTenants();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken) return;
        const timer = setTimeout(() => {
            fetchVpnUsers(false);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vpnUsersSearch]);

    const fetchVpnUsers = async (loadMore = false) => {
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setVpnUsersOffset(0);
        }

        try {
            const currentOffset = loadMore ? vpnUsersOffset : 0;
            const data = await getVpnUsers(accessToken, {
                limit: vpnUsersLimit,
                offset: currentOffset,
                search: vpnUsersSearch.trim() || undefined,
            });

            const items = data.items || [];
            const total = data.total || 0;

            if (loadMore) {
                setVpnUsers(prev => [...prev, ...items]);
            } else {
                setVpnUsers(items);
            }

            setVpnUsersTotal(total);
            setVpnUsersOffset(currentOffset + items.length);
        } catch (err) {
            console.error("Could not load VPN users:", err);
            toast.error(t("vpn.users.toast.loadFailed"));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchTenants = async () => {
        try {
            const data = await getTenants(accessToken);
            setTenants(data.items || data || []);
        } catch (err) {
            console.error("Could not load tenants:", err);
        }
    };

    // ============== CREATE VPN USER ==============

    const handleCreateModalOpen = () => {
        setCreateForm({
            username: "",
            email: "",
            full_name: "",
            allowed_tenants: [],
            password: "",
        });
        setShowCreateModal(true);
    };

    const handleCreateSubmit = async () => {
        if (!createForm.username.trim()) {
            toast.error(t("vpn.users.toast.usernameRequired"));
            return;
        }
        if (!createForm.email.trim()) {
            toast.error(t("vpn.users.toast.emailRequired"));
            return;
        }
        if (createForm.allowed_tenants.length === 0) {
            toast.error(t("vpn.users.toast.tenantRequired"));
            return;
        }
        if (!createForm.password.trim()) {
            toast.error(t("vpn.users.toast.passwordRequired"));
            return;
        }

        setCreating(true);
        try {
            await createVpnUser(accessToken, createForm);
            toast.success(t("vpn.users.toast.created"));
            setShowCreateModal(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("Could not create VPN user:", err);
            toast.error(err.response?.data?.detail || t("vpn.users.toast.createFailed"));
        } finally {
            setCreating(false);
        }
    };

    // ============== EDIT VPN USER ==============

    const handleEditModalOpen = (user) => {
        setEditingUser(user);
        setEditForm({
            email: user.email,
            full_name: user.full_name || "",
            allowed_tenants: user.allowed_tenants,
            is_active: user.is_active,
            password: "",
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        setUpdating(true);
        try {
            await updateVpnUser(accessToken, editingUser.id, editForm);
            toast.success(t("vpn.users.toast.updated"));
            setShowEditModal(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("Could not update VPN user:", err);
            toast.error(err.response?.data?.detail || t("vpn.users.toast.updateFailed"));
        } finally {
            setUpdating(false);
        }
    };

    // ============== DELETE VPN USER ==============

    const handleDeleteDialogOpen = (user) => {
        setDeletingUser(user);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            const result = await deleteVpnUser(accessToken, deletingUser.id);
            const username = result.username || deletingUser.username;
            toast.success(t("vpn.users.toast.deleted", { name: username }));
            setShowDeleteDialog(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("Could not delete VPN user:", err);
            toast.error(err.response?.data?.detail || t("vpn.users.toast.deleteFailed"));
        } finally {
            setDeleting(false);
        }
    };

    // ============== ACCESS LOGS ==============

    const fetchAccessLogs = async (user, searchTerm = "") => {
        setLoadingLogs(true);
        try {
            const logs = await getVpnUserAccessLogs(accessToken, user.id, {
                search: searchTerm.trim() || undefined,
            });
            setAccessLogs(logs);
        } catch (err) {
            console.error("Could not load access logs:", err);
            toast.error(t("vpn.users.toast.logsFailed"));
        } finally {
            setLoadingLogs(false);
        }
    };

    const handleLogsDialogOpen = async (user) => {
        setSelectedUser(user);
        setAccessLogsSearch("");
        setShowLogsDialog(true);
        await fetchAccessLogs(user, "");
    };

    // ============== VPN SESSION LOGS ==============

    const fetchSessions = async (loadMore = false) => {
        if (loadMore) {
            setLoadingMoreSessions(true);
        } else {
            setLoadingSessions(true);
            setSessionsOffset(0);
        }

        try {
            const currentOffset = loadMore ? sessionsOffset : 0;
            const data = await getVpnSessions(accessToken, {
                limit: sessionsLimit,
                offset: currentOffset,
                search: sessionsSearch.trim() || undefined,
            });
            const items = data.sessions || [];
            if (loadMore) {
                setSessions(prev => [...prev, ...items]);
            } else {
                setSessions(items);
            }
            setSessionsTotal(data.total || 0);
            setSessionsOffset(currentOffset + items.length);
        } catch (err) {
            console.error("Could not load session logs:", err);
            toast.error(t("vpn.users.toast.sessionsFailed"));
        } finally {
            setLoadingSessions(false);
            setLoadingMoreSessions(false);
        }
    };

    const handleSessionsDialogOpen = () => {
        setSessionsSearch("");
        setShowSessionsDialog(true);
        fetchSessions();
    };

    useEffect(() => {
        if (!showSessionsDialog || !accessToken) return;
        const timer = setTimeout(() => {
            fetchSessions(false);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionsSearch, showSessionsDialog, accessToken]);

    useEffect(() => {
        if (!showLogsDialog || !selectedUser || !accessToken) return;
        const timer = setTimeout(() => {
            fetchAccessLogs(selectedUser, accessLogsSearch);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessLogsSearch, showLogsDialog, selectedUser, accessToken]);

    // ============== TENANT SELECTION HELPERS ==============

    const handleTenantChange = (event, formType) => {
        const value = event.target.value;

        if (value.includes("*")) {
            if (formType === "create") {
                setCreateForm({ ...createForm, allowed_tenants: ["*"] });
            } else {
                setEditForm({ ...editForm, allowed_tenants: ["*"] });
            }
        } else {
            if (formType === "create") {
                setCreateForm({ ...createForm, allowed_tenants: value });
            } else {
                setEditForm({ ...editForm, allowed_tenants: value });
            }
        }
    };

    const getTenantNames = (allowedTenants) => {
        if (allowedTenants.includes("*")) {
            return t("vpn.users.all");
        }
        return allowedTenants
            .map((tid) => tenants.find((tn) => tn.id === tid)?.name || tid)
            .join(", ");
    };

    const formatDuration = (seconds) => {
        if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
            return "-";
        }
        const total = Math.max(0, Number(seconds));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        if (h > 0) {
            return t("vpn.users.duration.hms", { h, m, s });
        }
        if (m > 0) {
            return t("vpn.users.duration.ms", { m, s });
        }
        return t("vpn.users.duration.s", { s });
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canManageVpnUsers) {
        return (
            <Box sx={{ textAlign: "center", mt: 8, color: "var(--text-primary)" }}>
                <Typography variant="h6" sx={{ color: "var(--danger)" }}>
                    {t("vpn.users.noAccess")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ color: "var(--text-primary)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "var(--text-primary)" }}>
                    {t("vpn.users.title")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={handleSessionsDialogOpen}
                        sx={{ color: "var(--accent-navy-light)", borderColor: "var(--accent-navy-light)", "&:hover": { borderColor: "#79c0ff", color: "#79c0ff" } }}
                    >
                        {t("vpn.users.sessionsButton")}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateModalOpen}
                        sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        {t("vpn.users.newUser")}
                    </Button>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                }}
            >
                <Box sx={{ p: 2, borderBottom: "1px solid var(--border)" }}>
                    <TextField
                        sx={{ backgroundColor: "#fff" }}
                        size="small"
                        fullWidth
                        label={t("vpn.users.search")}
                        value={vpnUsersSearch}
                        onChange={(e) => setVpnUsersSearch(e.target.value)}
                    />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.col.username")}</TableCell>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.col.email")}</TableCell>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.col.fullName")}</TableCell>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.col.allowedTenants")}</TableCell>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.col.created")}</TableCell>
                            <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("vpn.users.col.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vpnUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ color: "var(--text-muted)", textAlign: "center", py: 4 }}>
                                    {t("vpn.users.empty")}
                                </TableCell>
                            </TableRow>
                        ) : (
                            vpnUsers.map((user) => (
                                <TableRow key={user.id} sx={{ backgroundColor: "var(--bg-card)", "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                                    <TableCell sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
                                        {user.username}
                                    </TableCell>
                                    <TableCell sx={{ color: "var(--text-primary)" }}>{user.email}</TableCell>
                                    <TableCell sx={{ color: "var(--text-primary)" }}>{user.full_name || "-"}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: "var(--accent-navy-light)" }}>
                                            {getTenantNames(user.allowed_tenants)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: "var(--text-primary)" }}>
                                        {formatDate(user.created_at, { dateStyle: "medium" })}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{ color: "#a371f7" }}
                                            onClick={() => handleLogsDialogOpen(user)}
                                            title={t("vpn.users.accessLogsTitle")}
                                        >
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{ color: "var(--accent-navy-light)", ml: 0.5 }}
                                            onClick={() => handleEditModalOpen(user)}
                                            title={t("vpn.users.editTitle")}
                                        >
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{ color: "var(--danger)", ml: 0.5 }}
                                            onClick={() => handleDeleteDialogOpen(user)}
                                            title={t("vpn.users.deleteTitle")}
                                        >
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {vpnUsers.length > 0 && vpnUsers.length < vpnUsersTotal && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => fetchVpnUsers(true)}
                        disabled={loadingMore}
                        sx={{
                            color: "var(--text-muted)",
                            borderColor: "var(--border)",
                            "&:hover": { borderColor: "var(--text-muted)" }
                        }}
                    >
                        {loadingMore
                            ? t("vpn.users.loading")
                            : t("vpn.users.loadMore", { shown: vpnUsers.length, total: vpnUsersTotal })
                        }
                    </Button>
                </Box>
            )}

            {/* CREATE MODAL */}
            <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {t("vpn.users.newUser")}
                    <IconButton size="small" onClick={() => setShowCreateModal(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("vpn.users.create.username")}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={createForm.username}
                        onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                        helperText={t("vpn.users.create.usernameHelp")}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label={t("vpn.users.field.email")}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={createForm.email}
                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label={t("vpn.users.col.fullName")}
                        fullWidth
                        variant="outlined"
                        value={createForm.full_name}
                        onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t("vpn.users.field.allowedTenants")}</InputLabel>
                        <Select
                            multiple
                            value={createForm.allowed_tenants}
                            onChange={(e) => handleTenantChange(e, "create")}
                            input={<OutlinedInput label={t("vpn.users.field.allowedTenants")} />}
                            renderValue={(selected) => getTenantNames(selected)}
                        >
                            {isSuperAdmin && (
                                <MenuItem value="*">
                                    <Checkbox checked={createForm.allowed_tenants.includes("*")} />
                                    <ListItemText primary={t("vpn.users.all")} />
                                </MenuItem>
                            )}
                            {tenants.map((tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id} disabled={createForm.allowed_tenants.includes("*")}>
                                    <Checkbox checked={createForm.allowed_tenants.includes(tenant.id)} />
                                    <ListItemText primary={tenant.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label={t("vpn.users.field.password")}
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleCreateSubmit}
                        disabled={creating}
                        sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        {creating ? t("vpn.users.create.creating") : t("vpn.users.create.submit")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* EDIT MODAL */}
            <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {t("vpn.users.edit.title")}
                    <IconButton size="small" onClick={() => setShowEditModal(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label={t("vpn.users.col.username")}
                        fullWidth
                        variant="outlined"
                        value={editingUser?.username || ""}
                        sx={{ mb: 2, mt: 1, backgroundColor: "#f5f5f5" }}
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("vpn.users.field.email")}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label={t("vpn.users.col.fullName")}
                        fullWidth
                        variant="outlined"
                        value={editForm.full_name}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t("vpn.users.field.allowedTenants")}</InputLabel>
                        <Select
                            multiple
                            value={editForm.allowed_tenants}
                            onChange={(e) => handleTenantChange(e, "edit")}
                            input={<OutlinedInput label={t("vpn.users.field.allowedTenants")} />}
                            renderValue={(selected) => getTenantNames(selected)}
                        >
                            {isSuperAdmin && (
                                <MenuItem value="*">
                                    <Checkbox checked={editForm.allowed_tenants.includes("*")} />
                                    <ListItemText primary={t("vpn.users.all")} />
                                </MenuItem>
                            )}
                            {tenants.map((tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id} disabled={editForm.allowed_tenants.includes("*")}>
                                    <Checkbox checked={editForm.allowed_tenants.includes(tenant.id)} />
                                    <ListItemText primary={tenant.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t("vpn.users.edit.status")}</InputLabel>
                        <Select
                            value={editForm.is_active}
                            label={t("vpn.users.edit.status")}
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value })}
                        >
                            <MenuItem value={true}>{t("vpn.users.active")}</MenuItem>
                            <MenuItem value={false}>{t("vpn.users.inactive")}</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label={t("vpn.users.edit.newPassword")}
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={editForm.password}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                        placeholder={t("vpn.users.edit.newPasswordPlaceholder")}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleEditSubmit}
                        disabled={updating}
                        sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        {updating ? t("vpn.users.edit.updating") : t("vpn.users.edit.submit")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog
                open={showDeleteDialog}
                onClose={() => !deleting && setShowDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { backgroundColor: "var(--bg-card)", border: "1px solid var(--danger)", borderRadius: "8px" }
                }}
            >
                <DialogTitle sx={{ color: "var(--danger)", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DeleteIcon />
                        {t("vpn.users.delete.title")}
                    </Box>
                    <IconButton size="small" onClick={() => setShowDeleteDialog(false)} disabled={deleting} sx={{ color: "var(--text-muted)" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "var(--text-primary)", mb: 2 }}>
                        <Trans i18nKey="vpn.users.delete.confirm" values={{ name: deletingUser?.username }} components={{ b: <strong /> }} />
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        sx={{ backgroundColor: "var(--danger)", "&:hover": { backgroundColor: "#da3633" } }}
                    >
                        {deleting ? t("vpn.users.delete.deleting") : t("vpn.users.delete.submit")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* VPN SESSIONS DIALOG */}
            <Dialog open={showSessionsDialog} onClose={() => setShowSessionsDialog(false)} maxWidth="xl" fullWidth
                PaperProps={{ sx: { backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px" } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: "var(--text-primary)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HistoryIcon sx={{ color: "var(--accent-navy-light)" }} />
                        {t("vpn.users.sessions.title")}
                    </Box>
                    <IconButton size="small" onClick={() => setShowSessionsDialog(false)} sx={{ color: "var(--text-muted)" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingSessions ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : sessions.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 6, color: "var(--text-muted)" }}>
                            {t("vpn.users.sessions.empty")}
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Box sx={{ p: 2,  borderBottom: "1px solid var(--border)" }}>
                                <TextField
                                    sx={{ backgroundColor: "#fff" }}
                                    size="small"
                                    fullWidth
                                    label={t("vpn.users.sessions.search")}
                                    value={sessionsSearch}
                                    onChange={(e) => setSessionsSearch(e.target.value)}
                                />
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.user")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.edge")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.engineerHub")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.originalHub")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.status")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.lastHeartbeat")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.connected")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.disconnected")}</TableCell>
                                        <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("vpn.users.sessions.col.duration")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ color: "var(--text-muted)", textAlign: "center", py: 3 }}>
                                                {t("vpn.users.sessions.notFound")}
                                            </TableCell>
                                        </TableRow>
                                    ) : sessions.map((s) => (
                                        <TableRow key={s.id} sx={{ backgroundColor: "var(--bg-card)", "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                                            <TableCell sx={{ color: "var(--text-primary)", fontWeight: 600 }}>{s.vpn_username || s.vpn_user_id}</TableCell>
                                            <TableCell sx={{ color: "var(--text-primary)" }}>{s.edge_asset_id || s.edge_device_id}</TableCell>
                                            <TableCell sx={{ color: "var(--accent-navy-light)", fontSize: "0.75rem", fontFamily: "monospace" }}>{s.engineer_hub}</TableCell>
                                            <TableCell sx={{ color: "var(--text-muted)", fontSize: "0.75rem", fontFamily: "monospace" }}>{s.original_hub}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={s.is_active ? t("vpn.users.active") : t("vpn.users.sessions.closed")}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: s.is_active ? "#1a4731" : "#2d1b1b",
                                                        color: s.is_active ? "#3fb950" : "var(--danger)",
                                                        fontWeight: 600,
                                                        fontSize: "0.7rem",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>
                                                {formatDate(s.last_heartbeat)}
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--text-primary)", fontSize: "0.75rem" }}>
                                                {formatDate(s.created_at)}
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>
                                                {formatDate(s.disconnected_at)}
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--text-primary)", fontSize: "0.75rem", fontWeight: 600 }}>
                                                {formatDuration(s.session_duration_seconds)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {sessions.length > 0 && sessions.length < sessionsTotal && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => fetchSessions(true)}
                                disabled={loadingMoreSessions}
                                sx={{ color: "var(--text-muted)", borderColor: "var(--border)", "&:hover": { borderColor: "var(--text-muted)" } }}
                            >
                                {loadingMoreSessions ? t("vpn.users.loading") : t("vpn.users.sessions.loadMore", { shown: sessions.length, total: sessionsTotal })}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* ACCESS LOGS DIALOG */}
            <Dialog open={showLogsDialog} onClose={() => setShowLogsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {t("vpn.users.logs.title", { name: selectedUser?.username })}
                    <IconButton size="small" onClick={() => setShowLogsDialog(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {loadingLogs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : accessLogs.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 4, color: "var(--text-muted)" }}>
                            {t("vpn.users.logs.empty")}
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Box sx={{ p: 2, borderBottom: "1px solid var(--border)" }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label={t("vpn.users.logs.search")}
                                    value={accessLogsSearch}
                                    onChange={(e) => setAccessLogsSearch(e.target.value)}
                                />
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("vpn.users.logs.col.edgeId")}</TableCell>
                                        <TableCell>{t("vpn.users.logs.col.ip")}</TableCell>
                                        <TableCell>{t("vpn.users.logs.col.connectTime")}</TableCell>
                                        <TableCell>{t("vpn.users.logs.col.disconnected")}</TableCell>
                                        <TableCell>{t("vpn.users.logs.col.duration")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {accessLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: "center", py: 3, color: "var(--text-muted)" }}>
                                                {t("vpn.users.logs.notFound")}
                                            </TableCell>
                                        </TableRow>
                                    ) : accessLogs.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>{log.edge_id || "-"}</TableCell>
                                            <TableCell>{log.ip_address || "-"}</TableCell>
                                            <TableCell>
                                                {formatDate(log.access_time)}
                                            </TableCell>
                                            <TableCell>
                                                {log.disconnect_time
                                                    ? formatDate(log.disconnect_time)
                                                    : t("vpn.users.logs.stillConnected")}
                                            </TableCell>
                                            <TableCell>{formatDuration(log.session_duration_seconds)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}